import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode, useId } from 'react';

import {
  type FeatureKind,
  isFeatureShown,
  showStaticFeatures,
} from '../../features/feature-badges/constants';
import { cssVar } from '../../theme/tokens';
import { Card, type CardProps } from './Card';
import { FeatureBadge } from './FeatureBadge';

export interface FeatureSectionProps extends Omit<CardProps, 'title' | 'badge' | 'variant'> {
  kind: FeatureKind;
  title: ReactNode;
  /**
   * `card` (default): a Card with the badge beside its title. `plain`: a heading row and the
   * content, for a group inside another surface (a list inside a card, a row of buttons).
   */
  variant?: 'card' | 'plain';
}

/**
 * A card or section with a feature badge beside its title (outside the heading, so the heading's
 * name stays the title). Static sections disappear when static features are switched off.
 */
export function FeatureSection({
  kind,
  title,
  variant = 'card',
  titleAs = 'h2',
  description,
  actions,
  children,
  ...cardProps
}: FeatureSectionProps) {
  const headingId = useId();
  if (!isFeatureShown(kind)) return null;
  const badge = <FeatureBadge kind={kind} />;

  if (variant === 'plain') {
    return (
      <Box component="section" aria-labelledby={headingId} sx={cardProps.sx}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography id={headingId} variant="h4" component={titleAs}>
            {title}
          </Typography>
          {badge}
          {actions ? <Box sx={{ ml: 'auto' }}>{actions}</Box> : null}
        </Stack>
        {description ? (
          <Typography sx={{ mt: 1, color: cssVar('text-muted') }}>{description}</Typography>
        ) : null}
        {children ? <Box sx={{ mt: 3 }}>{children}</Box> : null}
      </Box>
    );
  }

  return (
    <Card
      title={title}
      titleAs={titleAs}
      badge={badge}
      description={description}
      actions={actions}
      {...cardProps}
    >
      {children}
    </Card>
  );
}

export type KindlessSectionProps = Omit<FeatureSectionProps, 'kind'>;

/** A card or section of sample data with the Static badge; hidden when the flag is off. */
export function StaticSection(props: KindlessSectionProps) {
  return <FeatureSection kind="static" {...props} />;
}

/** A card or section that is new in v3 and runs on today's API, with the New badge. */
export function NewSection(props: KindlessSectionProps) {
  return <FeatureSection kind="new" {...props} />;
}

/** Renders its children only while static features are shown (static buttons in a dynamic bar). */
export function StaticOnly({ children }: { children: ReactNode }) {
  return showStaticFeatures() ? children : null;
}
